import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import herdTagService from '../../services/herdTagService';
import {
  calculateAgeMonths,
  formatSpeciesLifecycleSummary,
  getLifecycleTierName,
  getSpeciesProfile,
  listLifecycleTierNamesForSpecies,
  normalizeSpeciesSlug,
  resolveLifecycleTierLabels,
  LIFECYCLE_TIERS,
} from './core';
import { computeAgeClass } from './computeAgeClass';

const SpeciesLifecycleContext = createContext(null);

export function SpeciesLifecycleProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: ['species-lifecycle-config'],
    queryFn: () => herdTagService.getSpeciesLifecycleConfig(),
    enabled: isAuthenticated,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
  });

  const profiles = query.data?.profiles ?? {};
  const speciesSlugs = query.data?.species_slugs ?? [];
  const genericAgeClass = query.data?.generic_age_class ?? null;

  const value = useMemo(
    () => ({
      config: query.data ?? null,
      profiles,
      speciesSlugs,
      genericAgeClass,
      lifecycleTiers: query.data?.lifecycle_tiers ?? LIFECYCLE_TIERS,
      isLoading: query.isLoading,
      isReady: !!query.data && !query.isLoading,
      normalizeSpeciesSlug,
      getTierName: (speciesSlug, tier) => getLifecycleTierName(profiles, speciesSlug, tier),
      getProfile: (speciesSlug) => getSpeciesProfile(profiles, speciesSlug),
      listTierNamesForSpecies: (speciesSlug) => listLifecycleTierNamesForSpecies(profiles, speciesSlug),
      resolveTierLabels: (params) => resolveLifecycleTierLabels({ profiles, ...params }),
      formatSpeciesSummary: (category) => formatSpeciesLifecycleSummary(category, profiles),
      computeAgeClass: (input) => computeAgeClass(input, profiles, genericAgeClass),
      calculateAgeMonths,
    }),
    [query.data, query.isLoading, profiles, speciesSlugs, genericAgeClass]
  );

  return (
    <SpeciesLifecycleContext.Provider value={value}>
      {children}
    </SpeciesLifecycleContext.Provider>
  );
}

export function useSpeciesLifecycle() {
  const ctx = useContext(SpeciesLifecycleContext);
  if (!ctx) {
    throw new Error('useSpeciesLifecycle must be used within SpeciesLifecycleProvider');
  }
  return ctx;
}

export function useSpeciesLifecycleOptional() {
  return useContext(SpeciesLifecycleContext);
}
